import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";

import { Button, Icon, InputField, SettingsList, Text, View } from "../base";
import { Dialog } from "../base/Dialog";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { UserOrganization } from "../../redux/reducers/userSlice";
import { sessionActions } from "../../redux/reducers/sessionSlice";
import { useAppDispatch, useAppSelector } from "../../redux";

type OrganizationsCardProps = {
  organizations: UserOrganization[];
};

export const OrganizationsCard = ({ organizations }: OrganizationsCardProps) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const currentOrganizationId = useAppSelector((state) => state.session.currentOrganizationId);
  const defaultOrganizationId = user?.defaultOrganizationId;

  const [openMenuOrgId, setOpenMenuOrgId] = useState<string | null>(null);
  const [copiedOrgId, setCopiedOrgId] = useState<string | null>(null);
  const [leavingOrgId, setLeavingOrgId] = useState<string | null>(null);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Close kebab menu on outside click.
  useEffect(() => {
    if (!openMenuOrgId) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && !triggerRefs.current[openMenuOrgId]?.contains(target)) {
        setOpenMenuOrgId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenuOrgId]);

  const toggleMenu = (orgId: string) => {
    if (openMenuOrgId === orgId) {
      setOpenMenuOrgId(null);
      setMenuPosition(null);
      return;
    }
    const trigger = triggerRefs.current[orgId];
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpenMenuOrgId(orgId);
  };

  if (!organizations.length) {
    return null;
  }

  // Switching orgs in the app is purely local — update Redux + persisted
  // selection, then reload so queries refetch against the new X-Org-Id.
  const onClickSwitch = (org: UserOrganization) => {
    if (org.id === currentOrganizationId) return;
    dispatch(sessionActions.setCurrentOrganization({ id: org.id }));
    window.location.reload();
  };

  const onClickCopyId = async (org: UserOrganization) => {
    try {
      await navigator.clipboard.writeText(org.id);
      setCopiedOrgId(org.id);
      setTimeout(() => setCopiedOrgId((current) => (current === org.id ? null : current)), 1500);
    } catch {
      // navigator.clipboard can fail in non-secure contexts; nothing else to do.
    }
  };

  const onClickLeave = async (org: UserOrganization) => {
    setOpenMenuOrgId(null);
    if (leavingOrgId) return;
    if (!window.confirm(`Leave ${org.name}? You will lose access to this organization.`)) return;
    setLeavingOrgId(org.id);
    setLeaveError(null);
    try {
      await dispatch(sessionActions.leaveOrganization(org.id)).unwrap();
      window.location.reload();
    } catch (err) {
      setLeavingOrgId(null);
      setLeaveError(err instanceof Error ? err.message : "Could not leave organization");
    }
  };

  const onClickSetDefault = async (org: UserOrganization) => {
    setOpenMenuOrgId(null);
    if (settingDefaultId || org.id === defaultOrganizationId) return;
    setSettingDefaultId(org.id);
    setDefaultError(null);
    try {
      await dispatch(sessionActions.setDefaultOrganization(org.id)).unwrap();
    } catch (err) {
      setDefaultError(err instanceof Error ? err.message : "Could not update default organization");
    } finally {
      setSettingDefaultId(null);
    }
  };

  const closeCreateDialog = () => {
    if (isCreating) return;
    setIsCreateOpen(false);
    setNewOrgName("");
    setCreateError(null);
  };

  const onClickCreate = async () => {
    const name = newOrgName.trim();
    if (!name || isCreating) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      await dispatch(sessionActions.createOrganization(name)).unwrap();
      window.location.reload();
    } catch (err) {
      setIsCreating(false);
      setCreateError(err instanceof Error ? err.message : "Could not create organization");
    }
  };

  return (
    <OrganizationSettingsTab
      title="Organizations"
      count={organizations.length}
    >
      <SettingsList>
        {organizations.map((org, index) => {
          const isCurrent = org.id === currentOrganizationId;
          const isDefault = org.id === defaultOrganizationId;
          const canSwitch = !isCurrent;
          // Owners cannot leave their org — they'd leave it ownerless. The
          // transfer-ownership flow (Members admin) demotes them to admin
          // first, after which they can use this action.
          const canLeave = org.role !== "owner" && organizations.length > 1;
          const canSetDefault = !isDefault;
          const isLeaving = leavingOrgId === org.id;
          const isSettingDefault = settingDefaultId === org.id;
          // The kebab is disabled only for the default org that you either own
          // or is your only one — explain the dead-end rather than leaving a
          // silent, unclickable button.
          const hasNoMenuActions = !canLeave && !canSetDefault;
          const menuTitle = !hasNoMenuActions
            ? "More actions"
            : org.role === "owner"
              ? "This is your default organization, and you can't leave one you own"
              : "This is your default organization, and your only one";

          return (
            <View
              key={org.id}
              className={clsx("flex items-center gap-3 px-4 py-3", index > 0 && "border-t border-gray-200 dark:border-gray-700")}
            >
              {/* Left: check (current) or empty-circle switch affordance */}
              <View
                role={canSwitch ? "button" : undefined}
                onClick={canSwitch ? () => onClickSwitch(org) : undefined}
                title={isCurrent ? "Currently operating here" : "Switch"}
                className={clsx("flex-shrink-0 w-5 flex items-center justify-center", canSwitch && "cursor-pointer")}
              >
                <Icon
                  name={isCurrent ? "check" : "circle"}
                  size={16}
                  className={clsx(
                    isCurrent ? "text-brand dark:text-cyan-400" : "text-gray-300 dark:text-gray-600",
                    canSwitch && "hover:text-brand dark:hover:text-cyan-400 transition-colors",
                  )}
                />
              </View>

              {/* Middle: name + role */}
              <View className="flex flex-col min-w-0 flex-1">
                <View className="flex flex-row items-center gap-2 min-w-0">
                  <Text
                    variant="body"
                    className="font-semibold truncate"
                  >
                    {org.name}
                  </Text>
                  {isDefault && (
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand/10 text-brand dark:bg-cyan-400/20 dark:text-cyan-400 flex-shrink-0"
                      title="This is the organization you'll land in on a fresh client"
                    >
                      Default
                    </span>
                  )}
                </View>
                <Text
                  variant="muted"
                  className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500"
                >
                  {org.role}
                </Text>
              </View>

              {/* Right: copy-id + kebab menu trigger. The menu itself is portaled
                  (SettingsList has overflow:hidden for its rounded corners, which
                  would clip us). */}
              <View className="flex-shrink-0 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onClickCopyId(org)}
                  title={copiedOrgId === org.id ? "Copied!" : "Copy organization ID"}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <Icon
                    name={copiedOrgId === org.id ? "check" : "copy"}
                    size={16}
                    className={clsx(copiedOrgId === org.id && "text-brand dark:text-cyan-400")}
                  />
                </button>
                {/* Always rendered — keeps the copy button aligned across rows even
                    when this org has no menu actions (owner of the default org). */}
                <button
                  ref={(el) => {
                    triggerRefs.current[org.id] = el;
                  }}
                  type="button"
                  onClick={() => toggleMenu(org.id)}
                  disabled={!!leavingOrgId || !!settingDefaultId || hasNoMenuActions}
                  title={menuTitle}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 dark:disabled:hover:text-gray-500"
                >
                  <Icon
                    name="more-vertical"
                    size={16}
                  />
                </button>
              </View>
            </View>
          );
        })}
      </SettingsList>

      {openMenuOrgId &&
        menuPosition &&
        ReactDOM.createPortal(
          <View
            ref={menuRef}
            style={{ position: "fixed", top: menuPosition.top, right: menuPosition.right }}
            className="min-w-[11rem] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-[10000]"
          >
            {(() => {
              const org = organizations.find((o) => o.id === openMenuOrgId);
              if (!org) return null;
              const canSetDefault = org.id !== defaultOrganizationId;
              // Owners can also surface the Leave action — the API enforces the
              // "at least one owner must remain" rule and returns a readable error
              // that we display in the banner below if the caller is the sole owner.
              const canLeave = organizations.length > 1;
              const isLeaving = leavingOrgId === org.id;
              const isSettingDefault = settingDefaultId === org.id;
              return (
                <>
                  {canSetDefault && (
                    <button
                      type="button"
                      onClick={() => onClickSetDefault(org)}
                      disabled={isSettingDefault}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <Icon
                        name="check"
                        size={14}
                      />
                      {isSettingDefault ? "Setting…" : "Set as default"}
                    </button>
                  )}
                  {canLeave && (
                    <button
                      type="button"
                      onClick={() => onClickLeave(org)}
                      disabled={isLeaving}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left"
                    >
                      <Icon
                        name="logout"
                        size={14}
                      />
                      {isLeaving ? "Leaving…" : "Leave"}
                    </button>
                  )}
                </>
              );
            })()}
          </View>,
          document.body,
        )}

      {leaveError && <Text className="mt-2 text-xs text-red-500 dark:text-red-400">{leaveError}</Text>}
      {defaultError && <Text className="mt-2 text-xs text-red-500 dark:text-red-400">{defaultError}</Text>}

      <button
        type="button"
        onClick={() => setIsCreateOpen(true)}
        className="mt-2 flex flex-row items-center gap-3 px-4 py-2 self-start text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors group"
      >
        <View className="flex-shrink-0 w-5 flex items-center justify-center">
          <Icon
            name="add"
            size={16}
            className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-cyan-400 transition-colors"
          />
        </View>
        New Organization
      </button>

      <Dialog
        isOpen={isCreateOpen}
        onClose={closeCreateDialog}
        title="New Organization"
      >
        <View className="p-4 space-y-4">
          <Text variant="body">Enter a name for your new organization:</Text>
          <InputField
            value={newOrgName}
            onChange={setNewOrgName}
            placeholder="Organization name"
            className="w-full"
          />
          {createError && <Text className="text-xs text-red-500 dark:text-red-400">{createError}</Text>}
          <View className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              onClick={closeCreateDialog}
              isDisabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={onClickCreate}
              isDisabled={!newOrgName.trim() || isCreating}
            >
              {isCreating ? "Creating…" : "Create"}
            </Button>
          </View>
        </View>
      </Dialog>
    </OrganizationSettingsTab>
  );
};
